'use client';

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface DropdownProps {
  options: { label: string; onClick: () => void }[];
  slots?: {
    trigger?: {
      component: React.ReactNode;
      className?: string;
    };
    option?: {
      component: (option: {
        label: string;
        onClick?: () => void;
      }) => React.ReactNode;
      className?: string;
    };
  };
  className?: string;
  position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  onOpen?: () => void;
  onClose?: () => void;
  onSelect?: (option: { label: string; onClick: () => void }) => void;
  ariaLabel?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  slots = {},
  className = '',
  position = 'bottom-left',
  onOpen,
  onClose,
  onSelect,
  ariaLabel = 'Dropdown menu',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const updatePosition = () => {
    if (triggerRef.current && dropdownRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownWidth = dropdownRef.current.offsetWidth;
      const dropdownHeight = dropdownRef.current.offsetHeight;
      const positions = {
        'bottom-left': {
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
        },
        'bottom-right': {
          top: rect.bottom + window.scrollY,
          left: rect.right - dropdownWidth + window.scrollX,
        },
        'top-left': {
          top: rect.top - dropdownHeight + window.scrollY,
          left: rect.left + window.scrollX,
        },
        'top-right': {
          top: rect.top - dropdownHeight + window.scrollY,
          left: rect.right - dropdownWidth + window.scrollX,
        },
      };
      setDropdownPosition(positions[position]);
    }
  };

  const toggleDropdown = () => {
    if (!isOpen) {
      if (onOpen) onOpen();
      setIsOpen(true);
      // Update position after a small delay to ensure the dropdown is rendered
      setTimeout(updatePosition, 0);
    } else {
      if (onClose) onClose();
      setIsOpen(false);
    }
  };

  const closeMenu = (restoreFocus: boolean) => {
    setIsOpen(false);
    if (onClose) onClose();
    if (restoreFocus) {
      triggerRef.current
        ?.querySelector<HTMLElement>('button, [href], [tabindex]:not([tabindex="-1"])')
        ?.focus();
    }
  };

  const selectOption = (
    option: { label: string; onClick: () => void },
    restoreFocus: boolean
  ) => {
    option?.onClick?.();
    if (onSelect) onSelect(option);
    closeMenu(restoreFocus);
  };

  const getMenuItems = () =>
    Array.from(
      dropdownRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []
    );

  const handleItemKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    option: { label: string; onClick: () => void },
    index: number
  ) => {
    const items = getMenuItems();
    const focusItem = (next: number) => {
      event.preventDefault();
      items[(next + items.length) % items.length]?.focus();
    };

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        selectOption(option, true);
        break;
      case 'ArrowDown':
        focusItem(index + 1);
        break;
      case 'ArrowUp':
        focusItem(index - 1);
        break;
      case 'Home':
        focusItem(0);
        break;
      case 'End':
        focusItem(items.length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        closeMenu(true);
        break;
      case 'Tab':
        // The menu is portaled at the end of the body: refocus the trigger first so the
        // default Tab continues from there instead of from the end of the page
        closeMenu(true);
        break;
      default:
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      triggerRef.current &&
      !triggerRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
      if (onClose) onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [isOpen]);

  // Move focus into the menu on open: it is portaled, so it isn't next in tab order
  useEffect(() => {
    if (isOpen) {
      dropdownRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={triggerRef}>
      <div
        onClick={toggleDropdown}
        className={`cursor-pointer ${slots.trigger?.className || ''}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
      >
        {slots.trigger?.component || (
          <button type="button">Open Dropdown</button>
        )}
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
            }}
            className={`bg-white shadow-lg rounded-md p-2 ${className}`}
            role="menu"
          >
            {options.map((option, index) => (
              <div
                key={index}
                className={`cursor-pointer px-3 py-2 rounded-md hover:bg-neutral-100 transition-colors duration-150 ease-in-out ${
                  slots.option?.className || ''
                }`}
                onClick={() => selectOption(option, false)}
                onKeyDown={(event) => handleItemKeyDown(event, option, index)}
                role="menuitem"
                tabIndex={-1}
              >
                {slots.option?.component
                  ? slots.option.component(option)
                  : option.label}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default Dropdown;
